import {
  Add,
  DashboardCustomize,
  Functions,
  HelpOutline,
  Save,
  Settings,
  UnfoldLess,
  ListAlt,
  UnfoldMore,
} from '@mui/icons-material';
import { Button, Tooltip, Typography } from '@mui/material';
import { Timespan } from '@thingslog/repositories';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeframeChip } from '../widgets/TimeframeChip';

interface Props {
  dashboardName: string;
  dashboardPeriod?: Timespan | null;
  isDashboardSelectionOpen: boolean;
  isDashboardEditable: boolean;
  isEditingDashboard: boolean;
  isSavingDashboardLayout: boolean;
  isHeaderVisible?: boolean;
  onDashboardSelectionToggle: () => void;
  onHeaderToggle: () => void;
  onEditDashboard: () => void;
  onEditDashboardTimeframe: () => void;
  onAddWidget: () => void;
  onViewVariablesToggle: () => void;
  onEditDashboardLayout: () => void;
  onSaveDashboardLayout: () => void;
  onStartTour: () => void;
}

interface HeaderActionProps {
  icon: ReactNode;
  label: string;
  title?: string;
  disabled?: boolean;
  active?: boolean;
  highlighted?: boolean;
  iconOnly?: boolean;
  tourId?: string;
  disableTooltip?: boolean;
  onClick: () => void;
}

const DashboardHeaderAction: FC<HeaderActionProps> = ({
  icon,
  label,
  title,
  disabled = false,
  active = false,
  highlighted = false,
  iconOnly = false,
  tourId,
  disableTooltip = false,
  onClick,
}: HeaderActionProps) => {
  const action = (
    <span className="inline-flex shrink-0">
      <Button
        type="button"
        variant={highlighted ? 'contained' : 'outlined'}
        size="small"
        disableElevation
        disabled={disabled}
        startIcon={iconOnly ? undefined : icon}
        aria-label={label}
        data-dashboard-tour={tourId}
        sx={{
          height: 36,
          minWidth: iconOnly ? 36 : 'auto',
          width: iconOnly ? 36 : 'auto',
          borderRadius: 1,
          px: iconOnly ? 0 : 1.25,
          borderWidth: 1,
          borderStyle: 'solid',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: 1.25,
          ...(highlighted
            ? {
                borderColor: 'primary.main',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'primary.dark',
                  boxShadow: 'none',
                },
              }
            : active
            ? {
                borderColor: 'primary.light',
                bgcolor: '#eff6ff',
                color: 'primary.dark',
                '&:hover': {
                  borderColor: 'primary.light',
                  bgcolor: '#dbeafe',
                },
              }
            : {
                borderColor: 'grey.300',
                bgcolor: 'common.white',
                color: 'grey.700',
                '&:hover': {
                  borderColor: 'grey.400',
                  bgcolor: 'grey.50',
                },
              }),
          '& .MuiButton-startIcon': {
            mr: 0.75,
            ml: 0,
          },
        }}
        onClick={onClick}
      >
        {iconOnly ? icon : label}
      </Button>
    </span>
  );

  if (disableTooltip) {
    return action;
  }

  return <Tooltip title={title ?? label}>{action}</Tooltip>;
};

export const DashboardHeader: FC<Props> = ({
  dashboardName,
  dashboardPeriod,
  isDashboardSelectionOpen,
  isDashboardEditable,
  isEditingDashboard,
  isSavingDashboardLayout,
  isHeaderVisible = true,
  onDashboardSelectionToggle,
  onHeaderToggle,
  onEditDashboard,
  onEditDashboardTimeframe,
  onAddWidget,
  onViewVariablesToggle,
  onEditDashboardLayout,
  onSaveDashboardLayout,
  onStartTour,
}: Props) => {
  const { t } = useTranslation();

  return (
    <section className="w-full flex flex-row justify-between items-center gap-3 border-solid border-b border-x-0 border-t-0 border-gray-300 bg-white pb-2 pt-3 px-2">
      <div className="flex min-w-0 flex-row items-center gap-1.5">
        <DashboardHeaderAction
          icon={<ListAlt fontSize="small" />}
          label={t('dashboards_title', 'Dashboards')}
          title={
            isDashboardSelectionOpen
              ? t('dashboard_close_dashboards', 'Close dashboards')
              : t('dashboard_open_dashboards', 'Open dashboards')
          }
          active={isDashboardSelectionOpen}
          tourId="header-dashboards"
          onClick={onDashboardSelectionToggle}
        />
        {isDashboardEditable && (
          <DashboardHeaderAction
            icon={<Settings fontSize="small" />}
            label={t('dashboard_edit_dashboard', 'Edit dashboard')}
            iconOnly
            tourId="header-edit-dashboard"
            onClick={onEditDashboard}
          />
        )}
        <Typography variant="h4" className="truncate pl-2" data-dashboard-tour="header-title">
          {dashboardName}
        </Typography>
        <span data-dashboard-tour="header-period">
          <TimeframeChip
            timespan={dashboardPeriod}
            title={t('dashboard_edit_period', 'Edit dashboard period')}
            onClick={onEditDashboardTimeframe}
            className="ml-2 max-w-[14rem]"
          />
        </span>
      </div>
      <div className="flex shrink-0 flex-row flex-wrap items-center justify-end gap-1.5">
        {isDashboardEditable && (
          <DashboardHeaderAction
            icon={
              isEditingDashboard ? (
                <Save fontSize="small" />
              ) : (
                <DashboardCustomize fontSize="small" />
              )
            }
            label={
              isEditingDashboard
                ? t('dashboard_save_layout', 'Save layout')
                : t('dashboard_edit_layout', 'Edit layout')
            }
            disabled={isSavingDashboardLayout}
            active={isEditingDashboard}
            tourId="header-layout"
            onClick={isEditingDashboard ? onSaveDashboardLayout : onEditDashboardLayout}
          />
        )}
        <DashboardHeaderAction
          icon={<Add fontSize="small" />}
          label={t('dashboard_add_widget', 'Add widget')}
          highlighted
          tourId="header-add-widget"
          onClick={onAddWidget}
        />
        <DashboardHeaderAction
          icon={<Functions fontSize="small" />}
          label={t('dashboard_variables', 'Variables')}
          tourId="header-variables"
          onClick={onViewVariablesToggle}
        />
        <DashboardHeaderAction
          icon={isHeaderVisible ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
          label={
            isHeaderVisible
              ? t('dashboard_hide_app_header', 'Hide app header')
              : t('dashboard_show_app_header', 'Show app header')
          }
          iconOnly
          tourId="header-toggle-app-header"
          onClick={onHeaderToggle}
        />
        <DashboardHeaderAction
          icon={<HelpOutline fontSize="small" />}
          label={t('dashboard_start_tour', 'Start dashboard tour')}
          title={t('dashboard_start_tour', 'Start dashboard tour')}
          iconOnly
          tourId="header-help"
          disableTooltip
          onClick={onStartTour}
        />
      </div>
    </section>
  );
};
